import React from "react";
import { Button, Modal } from "antd";
import useResumeVerification from "./useResumeVerification";
import {
    ResumeCheckingProgress,
    ResumeDropzone,
    ResumeFailureNotice,
    ResumeRequirementExplainer,
    ResumeVerifiedNotice,
} from "./ResumeVerificationParts";

/**
 * Upload a resume, verify it can be read, and save it. On failure the
 * talent's current resume stays in place and they can try again.
 *
 * @param {Boolean} open
 * @param {Boolean} hasExistingResume
 * @param {Function} onVerified Called with (user, file) as soon as the resume passes
 * @param {Function} onClose
 */
const ResumeVerificationModal = ({ open, hasExistingResume = false, onVerified, onClose }) => {
    const verification = useResumeVerification({ onVerified });
    const { phase, file, error } = verification;
    const isChecking = phase === 'checking';

    const close = () => {
        if (!isChecking) {
            onClose?.();
        }
    };

    let actions = null;
    if (phase === 'select') {
        actions = (
            <>
                <Button onClick={close}>Cancel</Button>
                <Button type="primary" disabled={!file} onClick={verification.verify}>
                    {hasExistingResume ? 'Verify & replace resume' : 'Verify resume'}
                </Button>
            </>
        );
    } else if (phase === 'failed') {
        actions = (
            <>
                <Button onClick={close}>Cancel</Button>
                {error?.canRetry && file && (
                    <Button type="primary" onClick={verification.verify}>Try again</Button>
                )}
            </>
        );
    } else if (phase === 'verified') {
        actions = <Button type="primary" onClick={close}>Continue</Button>;
    }

    return (
        <Modal
            open={open}
            title={hasExistingResume ? 'Replace your resume' : 'Upload your resume'}
            width={840}
            centered
            destroyOnClose
            onCancel={close}
            afterClose={verification.reset}
            closable={!isChecking}
            maskClosable={!isChecking}
            keyboard={!isChecking}
            footer={null}>

            {phase === 'select' && (
                <>
                    <ResumeRequirementExplainer className="mb-4" />
                    <ResumeDropzone
                        file={file}
                        onSelect={verification.selectFile}
                        onRemove={verification.removeFile}
                    />
                    {hasExistingResume && (
                        <p className="m-0 mt-3 text-xs text-gray-500">
                            Your current resume stays on file unless the new one passes the check.
                        </p>
                    )}
                </>
            )}

            {isChecking && <ResumeCheckingProgress fileName={file?.name} />}

            {phase === 'failed' && (
                <>
                    <ResumeFailureNotice
                        error={error}
                        fileName={file?.name}
                        hasExistingResume={hasExistingResume}
                    />
                    <div className="mt-5">
                        <ResumeDropzone
                            onSelect={verification.selectFile}
                            prompt="Drop a different file here or"
                        />
                    </div>
                </>
            )}

            {phase === 'verified' && <ResumeVerifiedNotice fileName={file?.name} />}

            {actions && (
                <div className="mt-6 flex justify-end gap-3">{actions}</div>
            )}
        </Modal>
    );
};

export default ResumeVerificationModal;